import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const links = [
  { label: "Craft", href: "#manifesto" },
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#gallery" },
  { label: "Packages", href: "#packages" },
  { label: "Book", href: "#booking" },
];

const PHONE = "+ 1 (646) 400-9752";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[#222]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-5">
        <a href="#top" className="font-heading text-xl md:text-2xl tracking-tight leading-none" data-testid="navbar-brand">
          Bartending <span className="italic text-gold">Iz</span> Life
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="relative text-xs uppercase tracking-[0.25em] text-[#a0a0a0] hover:text-white transition-colors duration-300 group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href="tel:+16464009752"
            data-testid="navbar-phone-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-none border border-gold/60 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-black transition-all duration-300"
          >
            <Phone size={14} /> {PHONE}
          </a>
          <button
            className="md:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="navbar-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-t border-[#222]"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-heading text-2xl text-white/90 hover:text-gold transition-colors"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a href="tel:+16464009752" className="mt-2 text-xs uppercase tracking-[0.2em] text-gold">
                {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
